import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import RangeSlider from './RangeSlider';
import { UI_CONSTANTS } from '../../constants';
import {
  clampFilterPopupWidth,
  FILTER_POPUP_VIEWPORT_PADDING_PX
} from '../../constants/filterPopupLayout';
import {
  isGroupableField,
  isDateFieldEnhanced,
  getFieldType,
  isNestedGroupingField,
  getSourceIcon,
  formatPostTypeLabel
} from '../../utils/fieldConfig';
import '../../css/DropdownFilter.css';

function computePopupPlacement(popupEl, filterGroupEl) {
  const VP = FILTER_POPUP_VIEWPORT_PADDING_PX;
  const header = filterGroupEl.querySelector('.filter-header');
  const anchor = header || filterGroupEl;
  const rect = anchor.getBoundingClientRect();
  const width = clampFilterPopupWidth(rect.width);
  let left = Math.min(Math.max(rect.left, VP), window.innerWidth - VP - width);

  const gap = 8;
  const h = popupEl.offsetHeight;
  let top = rect.bottom + gap;

  const spaceBelow = window.innerHeight - VP - (rect.bottom + gap);
  const spaceAbove = rect.top - VP - gap;

  if (h > spaceBelow && spaceAbove > spaceBelow && rect.top - h - gap >= VP) {
    top = rect.top - h - gap;
  } else if (top + h > window.innerHeight - VP) {
    top = Math.max(VP, window.innerHeight - VP - h);
  }

  if (top < VP) top = VP;

  return { top, left, width };
}

export default function FilterPopup(props) {
  const {
    column,
    showFilterFor,
    setShowFilterFor,
    pendingFilters,
    setPendingFilters,
    filters,
    filterGroups,
    getDropdownOptionsForColumn,
    getNumberFieldConfigWithServerData,
    handleMultiSelectChange,
    handleFilterChange,
    handleRangeChange,
    applyFilters,
    clearFilter,
    isLoadingOptions = false
  } = props;

  // All hooks must be called before any early returns
  const [searchText, setSearchText] = useState('');
  const previousShowFilterFor = useRef(null);
  const popupRef = useRef(null);
  const [placement, setPlacement] = useState(null);

  // Helper function to detect which operator is used in a filter
  const getFilterOperator = (filter) => {
    if (!filter || typeof filter !== 'object') return 'in';
    if ('in' in filter) return 'in';
    if ('and' in filter) return 'and';
    if ('not' in filter) return 'not';
    if ('equals' in filter) return 'equals';
    return 'in';
  };

  // Memoize currentFilter to avoid dependency issues and use before definition
  const currentFilter = useMemo(() => {
    return pendingFilters[column.key] || {};
  }, [pendingFilters, column.key]);

  // Check if this is a nested grouping field (only these support AND/OR/NOT logic)
  const isNestedField = isNestedGroupingField(column.key);

  // Get current operator from pending filter, default to 'in'
  const currentOperator = useMemo(() => {
    if (!isNestedField) return 'in';
    return getFilterOperator(currentFilter);
  }, [currentFilter, isNestedField]);
  const [selectedOperator, setSelectedOperator] = useState(currentOperator);

  // Sync pendingFilters with current filters when popup opens
  useEffect(() => {
    const isOpening = showFilterFor === column.key && previousShowFilterFor.current !== column.key;

    if (isOpening) {
      const currentFilterValue = filters[column.key];
      if (currentFilterValue) {
        setPendingFilters(prev => ({
          ...prev,
          [column.key]: { ...currentFilterValue }
        }));
        if (isNestedField) {
          setSelectedOperator(getFilterOperator(currentFilterValue));
        } else {
          setSelectedOperator('in');
        }
      } else {
        setPendingFilters(prev => {
          const newPending = { ...prev };
          delete newPending[column.key];
          return newPending;
        });
        setSelectedOperator('in');
      }
    }

    previousShowFilterFor.current = showFilterFor;
  }, [showFilterFor, column.key, filters, setPendingFilters, isNestedField]);

  // Update selected operator when currentFilter changes (only for nested fields)
  useEffect(() => {
    if (isNestedField) {
      const operator = currentOperator;
      setSelectedOperator(operator);
    } else {
      setSelectedOperator('in');
    }
  }, [currentOperator, isNestedField]);

  const schedulePlacement = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const el = popupRef.current;
        if (!el || showFilterFor !== column.key) return;
        const filterGroupEl = el.closest('.filter-group');
        if (!filterGroupEl) return;
        setPlacement(computePopupPlacement(el, filterGroupEl));
      });
    });
  }, [showFilterFor, column.key]);

  useLayoutEffect(() => {
    if (showFilterFor !== column.key) {
      setPlacement(null);
      return undefined;
    }

    const el = popupRef.current;
    if (!el) return undefined;

    schedulePlacement();

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(() => schedulePlacement());
      ro.observe(el);
    }

    window.addEventListener('resize', schedulePlacement);
    window.addEventListener('scroll', schedulePlacement, true);

    return () => {
      ro?.disconnect();
      window.removeEventListener('resize', schedulePlacement);
      window.removeEventListener('scroll', schedulePlacement, true);
    };
  }, [showFilterFor, column.key, schedulePlacement]);

  // Early return AFTER all hooks
  if (showFilterFor !== column.key) return null;

  const appliedFilter = filters[column.key] || {};
  const hasAppliedFilter = Object.keys(appliedFilter).length > 0;
  const hasChanges = JSON.stringify(pendingFilters[column.key] || {}) !== JSON.stringify(filters[column.key] || {});

  const useDropdown = isGroupableField(column.key);
  let dropdownOptions = useDropdown ? getDropdownOptionsForColumn(column.key, filterGroups[column.key] || []) : [];


  dropdownOptions = (dropdownOptions || []).map(option => {
    if (typeof option === 'string') return { value: option, label: option, color: UI_CONSTANTS.DEFAULT_COLOR };
    return option;
  });

  if (column.key === 'PostType') {
    dropdownOptions = dropdownOptions.map(opt =>
      opt ? { ...opt, label: formatPostTypeLabel(opt.label ?? opt.value) } : opt
    );
  }

  const seenUnavailable = new Set();
  dropdownOptions = dropdownOptions.filter(option => {
    if (option == null) return false;
    const isUnavailable = (option.value === null || option.value === undefined || option.label === UI_CONSTANTS.UNAVAILABLE_LABEL || option.label === 'unavailable');
    if (isUnavailable) {
      if (seenUnavailable.has('unavailable')) return false;
      seenUnavailable.add('unavailable');
    }
    return true;
  });

  if (!Array.isArray(dropdownOptions)) dropdownOptions = [];

  const filteredDropdownOptions = !searchText.trim()
    ? dropdownOptions
    : dropdownOptions.filter(option => {
        if (option == null) {
          return UI_CONSTANTS.UNAVAILABLE_LABEL.toLowerCase().includes(searchText.toLowerCase());
        }
        const label = option.label !== undefined ? option.label : option.value !== undefined ? option.value : option;
        return String(label).toLowerCase().includes(searchText.toLowerCase());
      });

  const isDateColumn = isDateFieldEnhanced(column.key, column.label);

  const effectiveOperator = isNestedField ? selectedOperator : 'in';

  const popupStyle = placement
    ? {
        position: 'fixed',
        zIndex: 100,
        top: placement.top,
        left: placement.left,
        width: placement.width,
        boxSizing: 'border-box'
      }
    : {
        position: 'fixed',
        zIndex: 100,
        left: -99999,
        top: 0,
        width: clampFilterPopupWidth(1e6),
        visibility: 'hidden',
        pointerEvents: 'none',
        boxSizing: 'border-box'
      };

  return (
    <div
      ref={popupRef}
      className="filter-popup"
      style={popupStyle}
    >
      <div className="filter-popup-content">
        <div className="filter-popup-header">
          <h4>
            {column.label}
            {hasAppliedFilter && <span className="filter-active-indicator"> (Active)</span>}
          </h4>
          <button type="button" className="filter-close-btn" onClick={() => setShowFilterFor(null)}>×</button>
        </div>

        {useDropdown && Array.isArray(dropdownOptions) && dropdownOptions.length > 0 ? (
          <div className="filter-popup-field">
            <div className="multi-select-container">
              <div className="multi-select-search">
                <input
                  type="text"
                  placeholder="Search options..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  className="multi-select-search-input"
                />
                {searchText && (
                  <button
                    type="button"
                    className="multi-select-search-clear"
                    onClick={() => setSearchText('')}
                    title="Clear search"
                  >
                    ×
                  </button>
                )}
              </div>
              {isNestedField && (
                <div className="filter-logic-selector">
                  <div className="filter-logic-buttons">
                    <button
                      type="button"
                      className={`filter-logic-btn ${selectedOperator === 'in' ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedOperator('in');
                        const currentSelection = currentFilter.in || currentFilter.and || currentFilter.not || currentFilter.equals || [];
                        if (currentSelection.length > 0) {
                          handleMultiSelectChange(column.key, currentSelection, 'in');
                        }
                      }}
                      title="OR - Show items matching any selected option"
                    >
                      ANY
                    </button>
                    <button
                      type="button"
                      className={`filter-logic-btn ${selectedOperator === 'and' ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedOperator('and');
                        const currentSelection = currentFilter.in || currentFilter.and || currentFilter.not || currentFilter.equals || [];
                        if (currentSelection.length > 0) {
                          handleMultiSelectChange(column.key, currentSelection, 'and');
                        }
                      }}
                      title="AND - Show items matching all selected options"
                    >
                      All
                    </button>
                    <button
                      type="button"
                      className={`filter-logic-btn ${selectedOperator === 'not' ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedOperator('not');
                        const currentSelection = currentFilter.in || currentFilter.and || currentFilter.not || currentFilter.equals || [];
                        if (currentSelection.length > 0) {
                          handleMultiSelectChange(column.key, currentSelection, 'not');
                        }
                      }}
                      title="NOT - Show items not matching any selected option"
                    >
                      NO
                    </button>
                    <button
                      type="button"
                      className={`filter-logic-btn ${selectedOperator === 'equals' ? 'active' : ''}`}
                      onClick={() => {
                        setSelectedOperator('equals');
                        const currentSelection = currentFilter.in || currentFilter.and || currentFilter.not || currentFilter.equals || [];
                        if (currentSelection.length > 0) {
                          handleMultiSelectChange(column.key, currentSelection, 'equals');
                        }
                      }}
                      title="EXACT - Show items matching exactly selected options"
                    >
                      EXACT
                    </button>
                  </div>
                </div>
              )}
              <div className="multi-select-actions">
                <button type="button" className="multi-select-btn" onClick={() => {
                  const allValues = dropdownOptions.map(opt => (opt == null ? null : (opt.value !== undefined ? opt.value : opt)));
                  handleMultiSelectChange(column.key, allValues, effectiveOperator);
                }}>Select All</button>
                <button type="button" className="multi-select-btn" onClick={() => {
                  setPendingFilters(prev => {
                    const newFilters = { ...prev };
                    newFilters[column.key] = { [effectiveOperator]: [] };
                    return newFilters;
                  });
                }}>Clear All</button>
              </div>
              <div className="multi-select-options">
                {filteredDropdownOptions.length === 0 ? (
                  <div className="multi-select-no-results">No options match your search</div>
                ) : (
                  filteredDropdownOptions.map((option, index) => {
                  if (option == null) {
                    const value = null;
                    const label = UI_CONSTANTS.UNAVAILABLE_LABEL;
                    const color = UI_CONSTANTS.UNAVAILABLE_COLOR;
                    const currentSelection = currentFilter.in || currentFilter.and || currentFilter.not || currentFilter.equals || [];
                    const isSelected = currentSelection.includes(value);

                    return (
                      <div key={`unavailable-${index}`} className={`multi-select-option ${isSelected ? 'selected' : ''}`} onClick={() => {
                        const newSelection = isSelected ? currentSelection.filter(item => item !== value) : [...currentSelection, value];
                        handleMultiSelectChange(column.key, newSelection, effectiveOperator);
                      }}>
                        <div className="multi-select-checkbox">{isSelected && '✓'}</div>
                        <div className="multi-select-option-body">
                          <span className="multi-select-pill" style={{ backgroundColor: color }}>
                            {String(label)}
                          </span>
                        </div>
                      </div>
                    );
                  }

                  const value = option.value !== undefined ? option.value : option;
                  const label = option.label !== undefined ? option.label : option;
                  const color = option.color || UI_CONSTANTS.DEFAULT_COLOR;
                  const currentSelection = currentFilter.in || currentFilter.and || currentFilter.not || currentFilter.equals || [];
                  const isSelected = currentSelection.includes(value);

                  const iconSrc = getSourceIcon(value);

                  return (
                    <div key={value} className={`multi-select-option ${isSelected ? 'selected' : ''}`} onClick={() => {
                      const newSelection = isSelected ? currentSelection.filter(item => item !== value) : [...currentSelection, value];
                      handleMultiSelectChange(column.key, newSelection, effectiveOperator);
                    }}>
                      <div className="multi-select-checkbox">{isSelected && '✓'}</div>
                      <div className="multi-select-option-body">
                        {iconSrc && (
                          <img
                            src={iconSrc}
                            alt=""
                            className="multi-select-option-icon"
                            width={UI_CONSTANTS.ICON_SIZE_SMALL}
                            height={UI_CONSTANTS.ICON_SIZE_SMALL}
                          />
                        )}
                        <span className="multi-select-pill" style={{ backgroundColor: color }}>
                          {typeof label === 'object' ? JSON.stringify(label) : String(label)}
                        </span>
                      </div>
                    </div>
                  );
                  })
                )}
              </div>
            </div>
          </div>
        ) : useDropdown && (isLoadingOptions || !filterGroups[column.key]) ? (
          <div className="filter-popup-field">
            <div className="multi-select-container">
              <div className="multi-select-options" style={{ textAlign: 'center', padding: '24px 12px', color: '#64748b' }}>
                <div className="spinner" style={{ margin: '0 auto 8px' }}></div>
                Loading filter options…
              </div>
            </div>
          </div>
        ) : !isDateColumn && getFieldType(column.key) === 'text' ? (
          <div className="filter-popup-field">
            <div className="filter-input-with-mode">
              <input
                type="text"
                value={currentFilter.contains || currentFilter.equals || ''}
                onChange={(e) => {
                  const isEqualsMode = 'equals' in currentFilter;
                  const filterType = isEqualsMode ? 'equals' : 'contains';
                  handleFilterChange(column.key, filterType, e.target.value);
                }}
                placeholder="Search text..."
              />
              <button
                type="button"
                className={`filter-mode-btn ${('equals' in currentFilter) ? 'active' : ''}`}
                onClick={() => {
                  const currentValue = currentFilter.contains || currentFilter.equals || '';
                  const isCurrentlyEquals = 'equals' in currentFilter;

                  if (isCurrentlyEquals) {
                    handleFilterChange(column.key, 'equals', undefined);
                    handleFilterChange(column.key, 'contains', currentValue || '');
                  } else {
                    handleFilterChange(column.key, 'contains', undefined);
                    handleFilterChange(column.key, 'equals', currentValue || '');
                  }
                }}
                title={('equals' in currentFilter) ? 'Exact match (equals)' : 'Contains'}
              >
                {('equals' in currentFilter) ? '=' : '\u2248'}
              </button>
            </div>
          </div>
        ) : null}

        {!useDropdown && !isDateColumn && getFieldType(column.key) === 'number' && (
          <div className="filter-popup-field">
            <div className="range-filter-wrapper">
              {(() => {
                const config = getNumberFieldConfigWithServerData(column.key);
                const currentMin = currentFilter.min !== undefined ? currentFilter.min : config.min;
                const currentMax = currentFilter.max !== undefined ? currentFilter.max : config.max;
                return (
                  <RangeSlider min={config.min} max={config.max} step={config.step} value={[currentMin, currentMax]} onChange={(range) => handleRangeChange(column.key, range)} formatValue={config.formatValue} className="number-filter-slider" />
                );
              })()}
            </div>
          </div>
        )}

        {!useDropdown && isDateColumn && (
          <div className="filter-popup-field date-filter-rows">
            <div className="date-filter-row">
              <label htmlFor={`filter-date-from-${column.key}`}>From date</label>
              <input
                id={`filter-date-from-${column.key}`}
                type="date"
                value={currentFilter.fromdate || ''}
                onChange={(e) => handleFilterChange(column.key, 'fromdate', e.target.value)}
              />
            </div>
            <div className="date-filter-row">
              <label htmlFor={`filter-date-to-${column.key}`}>To date</label>
              <input
                id={`filter-date-to-${column.key}`}
                type="date"
                value={currentFilter.todate || ''}
                onChange={(e) => handleFilterChange(column.key, 'todate', e.target.value)}
              />
            </div>
          </div>
        )}

        <div className="filter-popup-actions">
          <button type="button" className="filter-clear-btn" onClick={() => clearFilter(column.key)} disabled={!hasAppliedFilter} title={hasAppliedFilter ? 'Clear this filter' : 'No filter to clear'}>Clear Filter</button>
          <button type="button" className={`filter-apply-btn ${hasChanges ? 'has-changes' : ''}`} onClick={applyFilters} disabled={!hasChanges}>Apply</button>
        </div>
      </div>
    </div>
  );
}
